import React from 'react';

import { useMutation } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { AccessibleAccountsDocument, SetAccountMfaRequirementDocument } from '../../graphql/__generated__/types';
import { refusalOf } from './mfaRequirement';

interface Props {
    accountId: string;
    accountName: string;
    /** What the account is about to become, not what it is. */
    required: boolean;
    onClose: () => void;
}

/**
 * The confirmation of « exiger » and « ne plus exiger le MFA sur un compte ».
 *
 * **A modal rather than an inline toggle**, for the reason the portal-access dialog
 * gives: the gesture changes a fact about a client, it is journalled, and it bears on
 * everybody attached to the account rather than on the person clicking.
 *
 * **ONE COMPONENT AND ONE MUTATION FOR BOTH DIRECTIONS, unlike its two neighbours**,
 * and the difference is worth stating because it looks like an inconsistency. Portal
 * access has two mutations because opening seeds default modules and demands that the
 * account-manager ladder resolve; modules have two because activation refuses on an
 * undelivered module while deactivation never does, so a row posted by hand stays
 * erasable. Here neither direction creates a dependent row nor forbids anything, and
 * lifting the requirement takes nothing away: a factor already registered stays
 * registered in the pool. There is no asymmetry to carry, so there is none to invent.
 *
 * **What the wording must not lose.** Requiring MFA does not enrol anybody: the people
 * already attached are asked for it at their next authenticated visit, and those who
 * already registered a factor notice nothing. And lifting the requirement does not
 * disable anybody's factor. An administrator who reads either one the other way spends
 * the afternoon looking for what they broke.
 *
 * **No reason field.** The portal-access closing takes one because `portal_enabled`
 * has no reading path and the *why* is the only trace of a decision nobody can
 * reconstruct. Here the journal already carries the direction, the account and the
 * number of people; a free-text box on a two-state policy collects « test » and « ok »,
 * which is worse in an audit log than no field at all.
 */
const MfaRequirementDialog = ({ accountId, accountName, required, onClose }: Props) => {
    /**
     * `AccountMfaRequirement` and `AccessibleAccount` are two GraphQL types over the
     * same row, so Apollo normalises them under different keys and the mutation result
     * cannot update the account sheet on its own. Refetching is what makes the panel
     * show the new state, exactly as the portal-access dialog does.
     */
    const [setRequirement, { data, loading, error }] = useMutation(SetAccountMfaRequirementDocument, {
        refetchQueries: [AccessibleAccountsDocument],
        awaitRefetchQueries: true,
    });

    const done = data?.setAccountMfaRequirement;

    const submit = () => {
        // The refusal is read off `error` below; without this catch the rejected
        // promise reaches the console as an unhandled one on every refusal.
        setRequirement({ variables: { organizationId: accountId, required } }).catch(() => {});
    };

    const title = required
        ? c('MFA requirement').t`Require multi-factor authentication`
        : c('MFA requirement').t`Stop requiring multi-factor authentication`;

    /**
     * The reach of the gesture, in people. Null is not zero: a total the API did not
     * give must not be printed as « nobody », which would read as a harmless gesture
     * on an account full of users.
     */
    const affected = done?.affectedUsers ?? null;

    return (
        <Modal open onClose={onClose} title={title} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">{accountName}</p>

                    {done ? (
                        <Alert type="success">
                            {done.mfaRequired
                                ? c('MFA requirement')
                                      .t`This account now requires multi-factor authentication. Nobody was enrolled just now: each person is asked to set it up on their next visit, and those who already carry a factor notice nothing.`
                                : c('MFA requirement')
                                      .t`This account no longer requires multi-factor authentication. Nobody's factor was disabled: whoever registered one keeps it, and keeps being asked for it at sign-in.`}
                            {affected !== null && (
                                <>
                                    {' '}
                                    {ngettext(
                                        msgid`It bears on ${affected} person attached to this account.`,
                                        `It bears on ${affected} people attached to this account.`,
                                        affected
                                    )}
                                </>
                            )}
                        </Alert>
                    ) : (
                        <>
                            {/* Before the button, never after: a warning read on the
                                confirmation of a done deed comes too late. */}
                            <Alert type="warning">
                                {required
                                    ? c('MFA requirement')
                                          .t`This enrols nobody by itself. Everyone attached to the account is asked to set up an authenticator app the next time they open their space, and cannot go further until they do. Those who already carry a factor are unaffected.`
                                    : c('MFA requirement')
                                          .t`This disables nobody's factor. Whoever already registered an authenticator app keeps it, and keeps being asked for a code at sign-in: only the demand made of those who have none is lifted.`}
                            </Alert>

                            {/* Said on the gesture rather than only in the ticket: an
                                operator who believes this is a security boundary will
                                answer a client's audit question wrongly. */}
                            {required && (
                                <p className="text-xs text-text-600">
                                    {c('MFA requirement')
                                        .t`Until a person registers a factor, the requirement is enforced by the portal screens. From the moment they do, it is the identity provider that asks for the code at every sign-in.`}
                                </p>
                            )}

                            {error && <RefusalNotice refusal={refusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button
                            /**
                             * Neither direction is destructive: requiring MFA protects,
                             * and lifting it removes no factor. A red button on either
                             * would push the reading the warnings above are correcting.
                             */
                            category="default"
                            loading={loading}
                            onClick={submit}
                        >
                            {required
                                ? c('MFA requirement action').t`Require it`
                                : c('MFA requirement action').t`Stop requiring it`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default MfaRequirementDialog;
